/**
 * PATH: packages/email-hub/src/feature/components/filter-sidebar/DateSection.tsx
 * FILE_ID: EMAILHUB-FILTERSIDEBAR-DATE-001
 * PURPOSE: Renders date range presets and date inputs.
 */
import { X } from 'lucide-react'

import { SectionHeader } from './SidebarChrome'
import type { SectionProps } from './types'

interface DateSectionProps extends SectionProps {
  gte?: string
  lte?: string
  onChange: (gte?: string, lte?: string) => void
}

export function DateSection({
  expanded,
  onToggle,
  gte,
  lte,
  onChange,
}: DateSectionProps) {
  const setRange = (days: number) => {
    const now = new Date()
    const from = new Date(now.getTime() - days * 86_400_000)
    onChange(from.toISOString(), undefined)
  }

  const isActive = !!(gte || lte)
  return (
    <div className="mb-3">
      <SectionHeader
        label="Date"
        expanded={expanded}
        onToggle={onToggle}
        count={isActive ? 1 : 0}
      />
      {expanded && (
        <div className="space-y-1">
          <div className="flex flex-wrap gap-1">
            {[
              { d: 1, label: '24h' },
              { d: 7, label: '7d' },
              { d: 30, label: '30d' },
              { d: 90, label: '90d' },
            ].map((p) => (
              <button
                key={p.d}
                onClick={() => setRange(p.d)}
                className="px-1.5 py-0.5 text-[10px] border border-border rounded hover:bg-accent text-muted-foreground"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="space-y-1">
            <label className="block text-[10px] text-muted-foreground">
              From
              <input
                type="date"
                value={gte ? gte.slice(0, 10) : ''}
                onChange={(e) =>
                  onChange(
                    e.target.value ? new Date(e.target.value).toISOString() : undefined,
                    lte,
                  )
                }
                className="w-full text-xs px-1.5 py-1 border border-border rounded bg-background text-foreground mt-0.5"
              />
            </label>
            <label className="block text-[10px] text-muted-foreground">
              To
              <input
                type="date"
                value={lte ? lte.slice(0, 10) : ''}
                onChange={(e) => {
                  // Treat the picked date as the END of that day so
                  // emails received during the day are included.
                  const v = e.target.value
                  onChange(
                    gte,
                    v ? new Date(`${v}T23:59:59.999Z`).toISOString() : undefined,
                  )
                }}
                className="w-full text-xs px-1.5 py-1 border border-border rounded bg-background text-foreground mt-0.5"
              />
            </label>
          </div>
          {isActive && (
            <button
              onClick={() => onChange(undefined, undefined)}
              className="text-[10px] text-muted-foreground hover:underline flex items-center gap-0.5"
            >
              <X className="h-2.5 w-2.5" /> Clear date
            </button>
          )}
        </div>
      )}
    </div>
  )
}
